import { Strings } from '../generated/strings';

/**
 * The units used in conversion.
 */
enum Unit {
    Byte = 0,

    // SI Prefix
    Kilobyte = 1,
    Megabyte = 2,
    Gigabyte = 3,
    Terabyte = 4,
    Petabyte = 5,
    Exabyte = 6,
    Zettabyte = 7,
    Yottabyte = 8,

    // IEC Prefix
    Kibibyte = 1,
    Mebibyte = 2,
    Gibibyte = 3,
    Tebibyte = 4,
    Pebibyte = 5,
    Exbiyte = 6,
    Zebibyte = 7,
    Yebibyte = 8
}

/**
 * The base of byte representation to use in conversion.
 */
export enum MediaConversionBase {
    Binary = 1024,
    Decimal = 1000
}

/**
 * Methods for converting media units (i.e. bytes) into display formats.
 * @dynamic
 */
export class MediaConversion {
    private static cachedIecUnitMap: { [index: number]: string };
    private static get iecUnitMap(): { [index: number]: string } {
        if (!MediaConversion.cachedIecUnitMap) {
            const strings = MsftSme.getStrings<Strings>().MsftSmeShell.Core;
            MediaConversion.cachedIecUnitMap = {
                0: strings.Units.MediaConversionB.label,
                1: strings.Units.MediaConversionKB.label,
                2: strings.Units.MediaConversionMB.label,
                3: strings.Units.MediaConversionGB.label,
                4: strings.Units.MediaConversionTB.label,
                5: strings.Units.MediaConversionPT.label,
                6: strings.Units.MediaConversionXB.label,
                7: strings.Units.MediaConversionZB.label,
                8: strings.Units.MediaConversionYB.label
            };
        }

        return MediaConversion.cachedIecUnitMap;
    }

    /**
     * Converts the supplied bytes into the appropriate display string.
     *
     * @param bytes The raw number of bytes.
     * @param base The byte representation base to use in conversion.
     */
    public static getConvertedValue(bytes: number, base: MediaConversionBase): string {
        if (bytes != null && !isNaN(bytes)) {
            const unit = MediaConversion.getUnit(bytes, base);
            const result = MediaConversion.convertValue(bytes, base, unit, false);
            const displayUnit = MediaConversion.iecUnitMap[unit];
            if (displayUnit) {
                return MsftSme.getStrings<Strings>()
                    .MsftSmeShell.Core.Units.MediaConversionFormat.message.format(result, displayUnit);
            }

            return MsftSme.getStrings<Strings>().MsftSmeShell.Core.Units.MediaConversionFormat.message.format(
                bytes, MediaConversion.iecUnitMap[0]);
        }

        return MsftSme.getStrings<Strings>().MsftSmeShell.Core.Units.MediaConversionUnknownFormat.message;
    }

    /**
     * Converts the raw bytes into the number corresponding to it's appropriate display value.
     *
     * @param bytes The raw number of bytes.
     * @param base the byte representation base to use in conversion.
     * @param unit The desired unit of conversion.
     * @param floor Whether or not to floor the result.
     */
    private static convertValue(bytes: number, base: MediaConversionBase, unit: Unit, floor?: boolean): number {
        const fixedDecimalPlaces = 2;
        const result = bytes / Math.pow(base, unit);
        if (floor) {
            return Math.floor(result);
        }

        return parseFloat(result.toFixed(fixedDecimalPlaces));
    }

    /**
     * Gets the appropriate unit for a number of raw bytes in a particular base.
     *
     * @param bytes The raw number of bytes.
     * @param base The byte representation base to use in conversion.
     */
    private static getUnit(bytes: number, base: MediaConversionBase): Unit {
        if (bytes === 0) {
            return <Unit>bytes;
        }

        let iterator = 0;
        while ((bytes / Math.pow(base, iterator)) >= base) {
            iterator++;
        }

        return <Unit>iterator;
    }
}

/**
 * Methods for converting percentages into display formats.
 */
export class PercentageConversion {
    /**
     * Converts the given value to a percentage.
     *
     * @param value the value to display as percent srring.
     */
    public static getPercentageDisplayValue(value: number): string {
        if (value != null && !isNaN(value)) {
            return MsftSme.getStrings<Strings>()
                .MsftSmeShell.Core.Units.PercentageConversionPercentFormat.message.format(value.toString());
        } else {
            return MsftSme.getStrings<Strings>().MsftSmeShell.Core.Units.PercentageConversionUnknownFormat.message;
        }
    }
}
